import {
  GridFour,
  Package,
  Scales,
  Storefront,
  TreeStructure,
  TrendUp,
  TShirt,
} from "@phosphor-icons/react"
import { useBlocker } from "@tanstack/react-router"

import { WorkspacePage } from "~/components/design-system/pages"

import { ConfirmationDialog } from "~/components/reusable/ConfirmationDialog"
import HeaderTabs, { type HeaderTab } from "~/components/reusable/HeaderTabs"
import useShowInternalFeatures from "~/hooks/useShowInternalFeatures"
import { useUser } from "~/providers/userProvider"
import { pluralize } from "~/utils/formatting"
import { formatName } from "~/utils/namingUtils"
import RangeTab from "../RangeTab"
import SetupCoverage from "./SetupCoverage"
import SetupProducts from "./products/SetupProducts"
import SetupRateOfSale from "./SetupRateOfSale"
import SetupRoutes from "./SetupRoutes"
import SetupRules from "./SetupRules"
import {
  SetupDraftsProvider,
  useSettingsLeaveGuard,
  useSettingsSaving,
  useSetupDraftCounts,
  useSetupDraftsBridge,
} from "./setupDrafts"
import SetupStores from "./stores/SetupStores"
import { StoreInspector } from "./stores/StoreInspector"

export const SETUP_SUB_TABS = [
  "coverage",
  "packages",
  "stores",
  "products",
  "sourcing",
  "rules",
  "rate-of-sale",
] as const

export type SetupSubTab = (typeof SETUP_SUB_TABS)[number]

export function isSetupSubTab(value: string): value is SetupSubTab {
  return (SETUP_SUB_TABS as readonly string[]).includes(value)
}

type SetupSubTabDef = {
  key: SetupSubTab
  label: string
  icon: React.ReactNode
  hakioOnly?: boolean
  inspector?: React.ComponentType
}

function getSubTabs(): SetupSubTabDef[] {
  return [
    { key: "coverage", label: "Coverage", icon: <GridFour /> },
    { key: "packages", label: "Packages", icon: <Package /> },
    { key: "stores", label: "Stores", icon: <Storefront />, inspector: StoreInspector },
    { key: "products", label: "Products", icon: <TShirt /> },
    { key: "rules", label: "Rules", icon: <Scales /> },
    { key: "rate-of-sale", label: `Rate of ${formatName("sale")}`, icon: <TrendUp /> },
    {
      key: "sourcing",
      label: "Store sourcing",
      icon: <TreeStructure />,
      hakioOnly: true,
    },
  ]
}

type SetupTabProps = {
  subTab: SetupSubTab
  onSubTabChange: (subTab: SetupSubTab) => void
  focusStoreId?: string
  focusRuleId?: string
}

export default function SetupTab(props: SetupTabProps) {
  return (
    <SetupDraftsProvider>
      <SetupTabInner {...props} />
    </SetupDraftsProvider>
  )
}

function SetupTabInner({
  subTab: requestedSubTab,
  onSubTabChange: setSubTab,
  focusStoreId,
  focusRuleId,
}: SetupTabProps) {
  const { permissions } = useUser()
  const [showInternalFeatures] = useShowInternalFeatures()

  const draftCounts = useSetupDraftCounts()
  const saving = useSettingsSaving()
  const leaveBlocker = useSettingsLeaveGuard(saving ? 0 : draftCounts.total)
  useBlocker({ shouldBlockFn: () => saving, enableBeforeUnload: false })
  const draftCountBySubTab: Record<SetupSubTab, number> = {
    coverage: draftCounts.packages,
    packages: draftCounts.packages,
    stores: draftCounts.stores,
    products: draftCounts.products,
    rules: draftCounts.rules,
    "rate-of-sale": draftCounts.rateOfSale,
    sourcing: 0,
  }

  const subTabs = getSubTabs().filter((tab) => !tab.hakioOnly || showInternalFeatures)
  const subTab = subTabs.some((tab) => tab.key === requestedSubTab) ? requestedSubTab : "coverage"
  const inspector = useSetupDraftsBridge(subTabs.find((tab) => tab.key === subTab)?.inspector)
  const headerTabs: HeaderTab[] = subTabs.map(({ key, label, icon, hakioOnly }) => ({
    label,
    hakioOnly,
    disabled: (hakioOnly && !permissions.isStaff) || (saving && key !== subTab),
    tooltip: saving && key !== subTab ? "Wait for the save to finish" : undefined,
    icon,
    count: draftCountBySubTab[key],
  }))

  return (
    <WorkspacePage contentClassName="p-0" inspector={inspector}>
      <div className="flex min-h-0 flex-1 flex-col bg-background">
        <div className="flex w-full items-center border-b border-base bg-base">
          <HeaderTabs
            tabs={headerTabs}
            selectedTab={subTabs.find((tab) => tab.key === subTab)?.label}
            onChange={(label) => {
              const tab = subTabs.find((t) => t.label === label)
              if (tab) setSubTab(tab.key)
            }}
            containerClassName="flex-1 border-b-0"
          />
        </div>

        <div className="flex min-h-0 flex-1 flex-col px-4 py-3.5">
          {subTab === "packages" && (
            <div className="min-h-0 flex-1 overflow-y-auto rounded-lg border border-base bg-base">
              <RangeTab />
            </div>
          )}
          {subTab === "sourcing" && permissions.isStaff && showInternalFeatures && <SetupRoutes />}
          {subTab === "rules" && (
            <SetupRules
              focusRuleId={focusRuleId}
              onGoToRateOfSale={() => setSubTab("rate-of-sale")}
            />
          )}
          {subTab === "rate-of-sale" && <SetupRateOfSale />}
          {subTab === "coverage" && <SetupCoverage />}
          {subTab === "stores" && <SetupStores focusStoreId={focusStoreId} />}
          {subTab === "products" && <SetupProducts />}
        </div>

        <ConfirmationDialog
          open={leaveBlocker.status === "blocked"}
          onOpenChange={(open) => !open && leaveBlocker.reset?.()}
          title="Leave without saving?"
          description={`You have ${pluralize(draftCounts.total, "unsaved change")} in Settings. Leaving discards them.`}
          confirmText="Leave without saving"
          variant="danger"
          onConfirm={() => leaveBlocker.proceed?.()}
          onCancel={() => leaveBlocker.reset?.()}
        />
      </div>
    </WorkspacePage>
  )
}
